<template>
    <div class="c-radiobutton"
         :class="[inheritedClass, {'has-validation-error': error }]">
        <input
            v-bind="inputAttributes"
            :id="id"
            type="radio"
            class="c-radiobutton__input sr-only"
            :name="name"
            :value="theValue"
            :checked="shouldBeChecked"
            @change="updateValue">
        <label class="c-radiobutton__label"
               :class="{ 'c-radiobutton__label--white': white }"
               :for="id">
            <CIcon v-if="hasIconBefore"
                   :name="iconName"
                   width="24"
                   height="24"
                   aria-hidden="true"
                   class="c-radiobutton__icon mt-auto mb-auto mr-10"
                   :fill="true"
                   :original="true"/>
            <span :class="labelClass">{{ label }}</span>
            <CIcon v-if="hasIconAfter"
                   :name="iconName"
                   width="24"
                   height="24"
                   aria-hidden="true"
                   class="c-radiobutton__icon absolute top-0 bottom-0 mt-auto mb-auto"
                   :fill="true"
                   :original="true"/>
        </label>
        <div v-if="showErrors && error"
             role="alert">
            <ol>
                <li class="text-red-500 text-12 font-medium pb-5 pt-3">
                    {{ error }}
                </li>
            </ol>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, useAttrs, inject } from 'vue';
import useFieldValidation from '@/project/form/useFieldValidation';
import { radioButtonInjectionKey, type FormRadioButtonContext } from './formTypes';
import * as z from 'zod';
import dictionaryService from '@/core/translation/dictionary.service';

const props = withDefaults(defineProps<{
    label?: string;
    name: string;
    white?: boolean;
    theValue: string | number | boolean | object;
    modelValue?: string | number | boolean | object;
    icon?: string;
    iconIsAfter?: boolean;
    labelClass?: string;
    rules?: z.Schema<any>;
    required?: boolean;
    errorLabelKey?: string;
    showErrors?: boolean;
    autoRegister?: boolean;
}>(), {
    label: ' ',
    modelValue: false,
    white: false,
    iconIsAfter: true,
    icon: undefined,
    labelClass: '',
    rules: undefined,
    errorLabelKey: 'Validation.Required',
    showErrors: true,
    autoRegister: true,
});

const attrs = useAttrs();
const { class: inheritedClass, ...inputAttributes } = attrs;

const emit = defineEmits(['change', 'input', 'update:modelValue']);

// Detect if we are inside an InputRadioGroup
const groupContext = inject<FormRadioButtonContext | null>(radioButtonInjectionKey, null);
const isInGroup = computed(() => groupContext !== null && !props.autoRegister);

const id = computed((): string => {
    return props.name + Math.random();
});

const iconName = computed(() => {
    return props.icon?.toLowerCase();
});

const hasIconBefore = computed((): boolean => {
    return !!props.icon && !props.iconIsAfter;
});

const hasIconAfter = computed((): boolean => {
    return !!props.icon && props.iconIsAfter;
});

const currentModelValue = computed(() => {
    if (isInGroup.value && groupContext) {
        return groupContext.modelValue;
    }
    return props.modelValue;
});

const shouldBeChecked = computed(() => {
    const value = currentModelValue.value;
    return value ? value === props.theValue : attrs.value === props.theValue;
});

// Standalone validator — only used when not inside a group
const standaloneValidator = () => {
    if (!props.required) return null;
    if (!props.modelValue) {
        return dictionaryService.get(props.errorLabelKey);
    }
    return null;
};

// When autoRegister=false (inside a group) we still call the composable to satisfy
// Vue's hooks rules, but pass register:false so the field is never added to Form.vue.
const { field, validate, setError } = useFieldValidation(
    props.name,
    props.label,
    standaloneValidator,
    computed(() => props.modelValue),
    { mode: 'lazy', register: props.autoRegister },
    id.value,
    'radio',
);

defineExpose({ validate, field, setError });

const error = computed(() => {
    if (isInGroup.value) return null;
    return field.error;
});

async function updateValue() {
    if (isInGroup.value && groupContext) {
        groupContext.updateValue(props.theValue);
        await groupContext.validateGroup();
    } else {
        emit('update:modelValue', props.theValue);
        validate();
    }
}
</script>

<style lang="less" scoped>

    .c-radiobutton__input {
        position: absolute;
        width: 0;
        height: 0;
        overflow: hidden;
        z-index: -1;
        opacity: 0;

        &:focus ~ .c-radiobutton__label::before {
            border: 1px solid theme('colors.gray.700');
        }
    }

    .c-radiobutton__label {
        @apply relative inline-flex items-center cursor-pointer;
        padding-left: 35px;

        .c-radiobutton__input:disabled + & {
            @apply text-gray-700 cursor-default;
        }
    }

    .c-radiobutton__label::before,
    .c-radiobutton__label::after {
        content: '';
    }

    .c-radiobutton__label::before {
        width: 2rem;
        height: 2rem;
        left: 0;
        @apply bg-gray-400 rounded-full absolute;

        .c-radiobutton__input:disabled + & {
            @apply bg-gray-400;
        }
    }

    &.c-radiobutton__label {
        &--white::before {
            @apply bg-white;
        }
    }

    .c-radiobutton__label::after {
        position: absolute;
        width: 1rem;
        height: 1rem;
        top: 0;
        left: 0.5rem;
        bottom: 0;
        margin: auto 0;
        transition: opacity 150ms ease-in-out;
        @apply bg-blue-500 oprema:bg-opremablue-500 hesa:bg-hesared-500 dias:bg-diasblue-500 rounded-full opacity-0;

        .c-radiobutton__input:disabled + & {
            @apply bg-gray-500;
        }
    }

    .c-radiobutton__input:checked + .c-radiobutton__label::after {
        @apply opacity-100;
    }

    .c-radiobutton__icon {
        right: -3rem;
    }

</style>
